// Governance & Compliance permission vocabulary (APP-004 R3).
//
// The backend is the only authority on these grants. Everything in this module
// is ADVISORY: it decides how an action is presented, never whether it is
// allowed.
//
// A membership either enumerates its governance grants or it does not. When it
// does (the edge derives them from the Studio role), an absent key is a real
// denial and the action is presented as unavailable. When it does not, the
// honest answer is "unknown": the action is offered and the backend's 403 is
// what turns it off. Never treat an empty list as a denial of everything, since
// that is also what an older gateway returns.
import { ApiError } from "@odingard/sdk-client";

export const GOVERNANCE_PERMISSIONS = {
  read: "policy_ai.governance.read",
  frameworksManage: "policy_ai.frameworks.manage",
  mappingsVerify: "policy_ai.mappings.verify",
  evidenceManage: "policy_ai.evidence.manage",
  evidenceApprove: "policy_ai.evidence.approve",
  exceptionsManage: "policy_ai.exceptions.manage",
  exceptionApprove: "policy_ai.exception.approve",
  riskAccept: "policy_ai.risk.accept",
  regulationsManage: "policy_ai.regulations.manage",
  impactVerify: "policy_ai.impact.verify",
  auditsManage: "policy_ai.audits.manage",
  findingsDecide: "policy_ai.findings.decide",
  reportsExport: "policy_ai.reports.export",
  proceduresRead: "procedures.read",
  proceduresAuthor: "procedures.author",
  proceduresApprove: "procedures.approve",
  proceduresExecute: "procedures.execute",
  proceduresVerify: "procedures.verify",
  proceduresAdminister: "procedures.administer",
} as const;

export type GovernancePermission = (typeof GOVERNANCE_PERMISSIONS)[keyof typeof GOVERNANCE_PERMISSIONS];

const LABELS: Record<GovernancePermission, string> = {
  "policy_ai.governance.read": "view governance data",
  "policy_ai.frameworks.manage": "manage frameworks and versions",
  "policy_ai.mappings.verify": "decide on requirement mappings",
  "policy_ai.evidence.manage": "manage evidence",
  "policy_ai.evidence.approve": "approve evidence",
  "policy_ai.exceptions.manage": "manage exceptions",
  "policy_ai.exception.approve": "approve exceptions",
  "policy_ai.risk.accept": "accept risk",
  "policy_ai.regulations.manage": "manage regulations",
  "policy_ai.impact.verify": "verify impact assessments",
  "policy_ai.audits.manage": "manage audit projects",
  "policy_ai.findings.decide": "decide on findings",
  "policy_ai.reports.export": "export reports",
  "procedures.read": "read procedures",
  "procedures.author": "author procedures",
  "procedures.approve": "approve procedures",
  "procedures.execute": "execute procedures",
  "procedures.verify": "verify procedure steps",
  "procedures.administer": "administer SOP AI intelligence",
};

export function permissionLabel(key: string): string {
  return LABELS[key as GovernancePermission] ?? "perform this action";
}

export type Certainty = "granted" | "unknown" | "denied";

export interface PermissionVerdict {
  key: GovernancePermission;
  /** Whether to offer the action at all. Never proof of authorization. */
  allowed: boolean;
  certainty: Certainty;
  /** Shown next to a disabled action, or as the reason a request failed. */
  reason: string;
  /** Backend error code when the backend is the one that said no. */
  code?: string;
}

/** A backend denial the UI has already observed for this key. */
export interface ObservedDenial {
  code: string;
  message: string;
}

// The key may end the sentence, so the trailing full stop must not be captured.
const MISSING_PERMISSION = /Missing required permission:\s*([a-z0-9_]+(?:\.[a-z0-9_]+)*)/i;

/**
 * Pulls the missing permission out of a backend 403. Policy AI answers
 * `forbidden` with `Missing required permission: <key>.`; newer routes may also
 * carry it in `details.permission`. Anything else yields null rather than a
 * guess.
 */
export function missingPermissionFrom(error: ApiError): string | null {
  if (error.status !== 403) return null;
  const details = (error as { details?: { permission?: unknown } }).details;
  if (details && typeof details.permission === "string") return details.permission;
  return MISSING_PERMISSION.exec(error.message)?.[1] ?? null;
}

/** Human explanation of a backend denial, keeping the code and key verbatim. */
export function describeDenial(error: ApiError): string {
  const key = missingPermissionFrom(error);
  if (key) {
    return `You don't have permission to ${permissionLabel(key)} (${key}). Ask an organization administrator to grant it.`;
  }
  if (error.status === 403) return `${error.message} (${error.code})`;
  return error.message;
}

/**
 * Whether this membership reports its governance grants at all. A membership
 * from a gateway that does not derive them carries none of these keys, and its
 * silence must not be read as a denial.
 */
export function enumeratesGovernanceGrants(membershipPermissions: readonly string[]): boolean {
  return membershipPermissions.some((p) => p.startsWith("policy_ai."));
}

/**
 * Advisory verdict from the Studio membership.
 *
 * `granted`: the membership lists the key.
 * `denied`: the backend already refused this key this session, or the
 *           membership enumerates its grants and this one is absent.
 * `unknown`: grants are not enumerated, so the action is offered and the
 *            backend decides when it is used.
 */
export function evaluatePermission(
  key: GovernancePermission,
  membershipPermissions: readonly string[],
  observed: Readonly<Record<string, ObservedDenial>>,
): PermissionVerdict {
  const denial = observed[key];
  if (denial) {
    return {
      key,
      allowed: false,
      certainty: "denied",
      reason: `You don't have permission to ${permissionLabel(key)} (${key}).`,
      code: denial.code,
    };
  }
  if (membershipPermissions.includes(key)) {
    return { key, allowed: true, certainty: "granted", reason: `Granted: ${key}.` };
  }
  if (enumeratesGovernanceGrants(membershipPermissions)) {
    return {
      key,
      allowed: false,
      certainty: "denied",
      reason: `You don't have permission to ${permissionLabel(key)} (${key}). Ask an organization administrator to grant it.`,
    };
  }
  return {
    key,
    allowed: true,
    certainty: "unknown",
    reason: `Requires ${key}. Your grants are decided by the backend when you act.`,
  };
}
